// One revision of a named blueprint, at its own address,
// `workspaces/blueprints/<name>/history/<revision>` (02-DESIGN/03-console/01-the-console.md
// § the address, § the blueprint's page; hq decision 0069 point 1): who
// saved it and when, what restoring it does in each workspace that uses the
// blueprint, and *Restore this version*, which saves it again as a new
// revision and returns to the blueprint's *History*. The blueprint's page
// asks before it is left with unsaved changes, so nothing here can drop them.

import { blueprint } from "@impire-io/sdk";
import { Link, useNavigate, useParams } from "react-router";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import {
  Confirm,
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { NameLink } from "../../ui/ident.tsx";
import {
  Code,
  EXPECT_REVISION_HEADER,
  SERVICE,
  Subjects,
  validName,
  type ChildrenReply,
  type Entry,
  type HistoryReply,
} from "../../wire/blueprint.ts";
import { useSurface } from "../surface.tsx";
import { consequences, slotEffects } from "./blueprint.ts";
import {
  BLUEPRINTS_TAB,
  blueprintPath,
  childPath,
  libraryOf,
  reachWords,
  restoreGuard,
  revisionOf,
} from "./library.ts";

export function BlueprintRevisionPage() {
  const { name = "", revision = "" } = useParams();
  return <Revision key={`${name}@${revision}`} name={name} revision={revisionOf(revision)} text={revision} />;
}

/** absent reads a not-found refusal as absence. */
function absent<T>(read: Promise<T>): Promise<T | undefined> {
  return read.catch((err: unknown) => {
    if (isRefusal(err, Code.notFound)) {
      return undefined;
    }
    throw err;
  });
}

function Revision({ name, revision, text }: { name: string; revision: number | undefined; text: string }) {
  const s = useSurface();
  const navigate = useNavigate();
  const ok = validName(name) && revision !== undefined;
  const [err, setErr] = useState<unknown>(undefined);
  const [current] = useLoad(`library:${name}`, () =>
    ok ? absent(s.bus.call<Entry>(SERVICE, Subjects.libraryGet(name), {})) : Promise.resolve(undefined),
  );
  const [history] = useLoad(`library-history:${name}`, () =>
    ok
      ? absent(s.bus.call<HistoryReply>(SERVICE, Subjects.libraryHistory(name), undefined))
      : Promise.resolve(undefined),
  );
  const [old] = useLoad(`library:${name}@${text}`, () =>
    ok
      ? absent(s.bus.call<Entry>(SERVICE, Subjects.libraryGet(name), { revision }))
      : Promise.resolve(undefined),
  );
  // Who a restore reaches: the workspaces that use the blueprint, each name a link to its page.
  const [rows] = useLoad("blueprint-children", () =>
    s.bus.call<ChildrenReply>(SERVICE, Subjects.children(), undefined),
  );
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "workspaces")}>Workspaces</Link>
      <span>/</span>
      <Link to={tenantPath(s.tenant, "workspaces")} state={{ tab: BLUEPRINTS_TAB }}>
        Blueprints
      </Link>
      <span>/</span>
      {validName(name) ? (
        <Link className="mono" to={blueprintPath(s.tenant, name)} state={{ tab: "history" }}>
          {name}
        </Link>
      ) : (
        <span className="mono">{name}</span>
      )}
      <span>/</span>
      <span>version {text}</span>
    </>
  );
  if (!ok) {
    return (
      <>
        <PageHead crumb={crumb} title={`Version ${text}`} />
        <Empty>No such version of {name}: a version is a whole number from 1.</Empty>
      </>
    );
  }
  if (history.state === "pending" || current.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the version" />
      </>
    );
  }
  if (history.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={`Version ${text}`} />
        <NotAllowed err={history.err} what="This version" />
      </>
    );
  }
  const picked = (history.value?.entries ?? []).find((e) => e.revision === revision);
  if (picked === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={`Version ${text}`} />
        <Empty>
          {name} has no version {text}.
        </Empty>
      </>
    );
  }
  const now =
    current.state === "ready" && current.value?.op === blueprint.EntryOp.put ? current.value : undefined;
  const was = libraryOf(old.state === "ready" ? old.value : undefined);
  const before = libraryOf(now);
  const lines =
    was === undefined
      ? []
      : [
          ...consequences(before === undefined ? undefined : { ...before, secrets: undefined }, {
            ...was,
            secrets: undefined,
          }),
          ...slotEffects(before?.secrets, was.secrets),
        ];
  const users = (rows.state === "ready" ? (rows.value.children ?? []) : []).filter(
    (r) => r.blueprint === name,
  );
  const restorable = picked.op === blueprint.EntryOp.put && picked.revision !== now?.revision;
  // A restore is written under the revision guard Save sends, and only once
  // the current version is read: never an unguarded overwrite.
  const guard = restoreGuard(current);
  const usersRead =
    rows.state === "ready"
      ? { state: "ready" as const, value: users.length }
      : rows.state === "pending"
        ? { state: "pending" as const }
        : { state: "refused" as const };
  const restore = () => {
    setErr(undefined);
    const doc = old.state === "ready" ? old.value?.document : undefined;
    if (doc === undefined || guard === undefined) {
      return;
    }
    s.bus.callText<Entry>(SERVICE, Subjects.librarySet(name), doc, { [EXPECT_REVISION_HEADER]: guard }).then(
      (e) => {
        void navigate(blueprintPath(s.tenant, name), {
          state: {
            tab: "history",
            said: `Version ${text} restored as version ${String(e.revision)}.`,
          },
        });
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  return (
    <>
      <PageHead
        crumb={crumb}
        title={`Version ${text}`}
        line={
          <>
            {picked.op === blueprint.EntryOp.delete ? "Deleted" : "Saved"}
            {picked.author !== undefined ? ` by ${picked.author}` : ""}
            {picked.created !== undefined ? ` ${when(picked.created)}` : ""}
            {picked.revision === now?.revision ? " · the version saved now" : ""}
          </>
        }
        action={
          restorable && guard !== undefined && old.state === "ready" && old.value !== undefined ? (
            <Confirm
              kind="default"
              label="Restore this version"
              consequence={`It is saved again as a new version, and ${reachWords(usersRead)}.`}
              onConfirm={restore}
            />
          ) : undefined
        }
      />
      <div className="stack">
        {current.state === "refused" && (
          <NotAllowed err={current.err} what="The version saved now, which a restore is checked against" />
        )}
        {err !== undefined &&
          (isRefusal(err, Code.conflict) ? (
            <Notice kind="bad" title="Someone saved this blueprint after you opened this version">
              Nothing was restored. Reload the page to see what restoring it does now.
            </Notice>
          ) : (
            <NotAllowed err={err} what="Restore" />
          ))}
        {current.state === "refused" ? null : picked.revision === now?.revision ? (
          <p className="muted">This is the version saved now: restoring it changes nothing.</p>
        ) : picked.op !== blueprint.EntryOp.put ? (
          <p className="muted">The blueprint was deleted here; there is nothing to restore.</p>
        ) : old.state === "pending" ? (
          <Pending what="Reading the version" />
        ) : old.state === "refused" ? (
          <NotAllowed err={old.err} what="This version" />
        ) : (
          <section className="section">
            <div className="section-head">
              <h2>What restoring it does</h2>
              {users.length > 0 && (
                <span className="small faint">
                  in{" "}
                  {users.map((u, i) => (
                    <span key={u.slug}>
                      {i > 0 && ", "}
                      <NameLink to={childPath(s.tenant, u.slug)}>{u.name ?? u.slug}</NameLink>
                    </span>
                  ))}
                </span>
              )}
            </div>
            {lines.length === 0 ? (
              <p className="small muted">Nothing it does not already do.</p>
            ) : (
              <ul className="small">
                {lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </>
  );
}
